import {
  type Alignment,
  FloatingArrow,
  FloatingPortal,
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
  useTransitionStyles,
} from "@floating-ui/react";
import React, { useRef, useState } from "react";

import { cn } from "@/lib/utils";

type Side = "top" | "side-start" | "bottom" | "side-end";
type AlignedPlacement = `${Side}-${Alignment}`;
type Placement = Side | AlignedPlacement;

interface TooltipProps {
  placement?: Placement;
  content: React.ReactNode;
  children: React.ReactElement<any>;
  disabled?: boolean;
  theme?: "light" | "dark";
}

const isRTL = true;

const themeStyles = {
  light: { box: "bg-white text-black shadow-lg", arrow: "fill-white" },
  dark: { box: "bg-gray-900 text-white", arrow: "fill-gray-900" },
};

export default function Tooltip({
  children,
  placement = "top",
  content,
  disabled = false,
  theme = "light",
}: TooltipProps) {
  const arrowRef = useRef(null);
  const [open, setOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    placement: placement
      .replace("side-start", isRTL ? "left" : "right")
      .replace("side-end", isRTL ? "right" : "left") as any,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(10),
      flip({
        crossAxis: placement.includes("-"),
        fallbackAxisSideDirection: "start",
        padding: 5,
      }),
      shift({ padding: 20 }),
      arrow({
        element: arrowRef,
      }),
    ],
  });

  const hover = useHover(context, {
    move: false,
    enabled: !!content && !disabled,
  });

  const focus = useFocus(context, { enabled: !!content && !disabled });
  const role = useRole(context, {
    role: "tooltip",
    enabled: !!content && !disabled,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    role,
  ]);

  const { isMounted, styles } = useTransitionStyles(context, { duration: 100 });

  const childRef = (
    children as React.ReactElement & { ref?: React.Ref<unknown> }
  ).ref;
  const mergedRef = useMergeRefs([refs.setReference, childRef ?? null]);

  return (
    <>
      {React.cloneElement(
        children,
        getReferenceProps({
          ...children.props,
          ref: mergedRef,
        }),
      )}
      {isMounted && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              ...styles,
              width: "auto",
              maxWidth: "28rem",
            }}
            {...getFloatingProps()}
            className={cn(
              "z-50 rounded-lg px-2 py-1 text-sm whitespace-pre-wrap",
              themeStyles[theme].box,
            )}
          >
            {content}
            <FloatingArrow
              ref={arrowRef}
              context={context}
              className={themeStyles[theme].arrow}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
